import React, { useState } from 'react';
import { Accident } from '../types';
import { ArrowLeft, MapPin, Navigation } from 'react-feather';
import './ReplacementRequestScreen.css';

interface ReplacementRequestScreenProps {
  accident: Accident;
  onBack: () => void;
  onSubmit: (formData: { name: string; email: string; phone: string }) => void;
}

export const ReplacementRequestScreen: React.FC<ReplacementRequestScreenProps> = ({
  accident,
  onBack,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {
      name: '',
      email: '',
      phone: ''
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-+()]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      // Simulate form submission delay
      setTimeout(() => {
        onSubmit(formData);
        setIsSubmitting(false);
      }, 800);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <div className="replacement-request-screen">
      <div className="request-header">
        <button 
          onClick={onBack}
          className="back-button"
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="request-title">Request Vehicle</h1>
      </div>

      <div className="request-content">
        {/* Accident Summary */}
        <div className="accident-summary-card">
          <h3 className="accident-title">{accident.title}</h3>
          
          <div className="accident-details">
            <div className="accident-detail">
              <MapPin size={14} className="detail-icon" />
              <span className="detail-text">{accident.location.address}</span>
            </div>
            
            <div className="accident-detail">
              <Navigation size={14} className="detail-icon" />
              <span className="detail-text">{accident.affectedLanes} lane(s) affected</span>
            </div>
            
            <div className="accident-detail">
              <div 
                className="severity-indicator"
                style={{ backgroundColor: getSeverityColor(accident.severity) }}
              />
              <span className="detail-text">
                {accident.severity.charAt(0).toUpperCase() + accident.severity.slice(1)} severity
              </span>
            </div>
          </div>
        </div>

        {/* Customer Details Form */}
        <div className="customer-details-card">
          <h2 className="card-title">Customer Details</h2>

          <form onSubmit={handleSubmit} className="customer-form">
            <div className="form-group">
              <label htmlFor="name" className="form-label">Full Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="Enter full name"
                disabled={isSubmitting}
              />
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter email address"
                disabled={isSubmitting}
              />
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="Enter phone number"
                disabled={isSubmitting}
              />
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="submit-loading">
                  <div className="submit-spinner" />
                  <span>Processing...</span>
                </div>
              ) : (
                <span>Submit Request</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}; 